import React, { useContext, useEffect, useState } from "react";
import SectionCard from "../components/SectionCard";
import { Label, Input, Select } from "../components/FormControls";
import { Building2, ShieldCheck, Wallet } from "lucide-react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../../../Context/GlobalContext";

import useOrderSettings from "../../../Services/OrderServices/OrderSettingsServices/useOrderSettings";
export default function ConfigurationSection({ userID }) {
  const {getDeliveryProvider, updateDeliveryProvider, getSettlementAccountList , addSettlementAccount, getOrganizationDetails,editOrganizationInfo}= useOrderSettings();

  const { api } = useContext(GlobalContext);

  // delivery provider
  const [deliveryType, setDeliveryType] = useState("");

  // bank
  const [bankList, setBankList] = useState([]);
  const [bankForm, setBankForm] = useState({ account: "", ifsc: "", bankName: "", bankHolderName: "" });

  // org info
  const [orgForm, setOrgForm] = useState({
    organizationName: "", gstNumber: "", payment_gateway: "RAZORPAY", brand_image: "",
  });

const loadDeliveryProvider = async () => {
  if (!userID) return;

  try {
    const result =
      await getDeliveryProvider(userID);

    if (result.success) {
      setDeliveryType(result.deliveryPartner);
    } else {
      setDeliveryType("");
      // optional:
      // toast.error(result.message);
    }
  } catch {
    setDeliveryType("");
  }
};

const setProvider = async (partnerType) => {
  try {
    const result =
      await updateDeliveryProvider({
        userid: userID,
        deliveryPartner: partnerType,
      });

    if (result.success) {
      toast.success(result.message);
      setDeliveryType(partnerType);
    } else {
      toast.error(result.message);
    }
  } catch {
    toast.error("Failed");
  }
};


 const loadBanks = async () => {
  if (!userID) return;

  try {
    const result =
      await getSettlementAccountList(userID);

    if (result.success) {
      setBankList(result.data);
    } else {
      setBankList([]);
      // optional:
      // toast.error(result.message);
    }
  } catch {
    setBankList([]);
  }
};

 const addBank = async () => {
  const f = bankForm;

  if (
    !f.account ||
    !f.ifsc ||
    !f.bankName ||
    !f.bankHolderName
  )
    return toast.error("Fill all bank fields");

  try {
    const result =
      await addSettlementAccount({
        userid: userID,
        account: f.account,
        ifsc: f.ifsc,
        bankName: f.bankName,
        bankHolderName: f.bankHolderName,
      });

    if (result.success) {
      toast.success(result.message);

      setBankForm({
        account: "",
        ifsc: "",
        bankName: "",
        bankHolderName: "",
      });

      loadBanks();
    } else {
      toast.error(result.message);
    }
  } catch {
    toast.error("Failed");
  }
};


 const loadOrg = async () => {
  if (!userID) return;

  try {
    const result =
      await getOrganizationDetails(userID);

    if (result.success) {
      const d = result.data;

      setOrgForm((prev) => ({
        ...prev,
        organizationName:
          d.organization_name || "",
        gstNumber: d.gst || "",
        payment_gateway:
          d.payment_gateway || "RAZORPAY",
        brand_image: d.brand_image || "",
      }));
    }
  } catch {
    // optional: toast.error("Failed to load organization details");
  }
};

 const saveOrg = async () => {
  try {
    const body = {
      userid: userID,
      organizationName: orgForm.organizationName,
      gst: orgForm.gstNumber,
      pan_no: "",
      brand_image: orgForm.brand_image,
      payment_gateway: orgForm.payment_gateway,
    };

    const result =
      await editOrganizationInfo(body);

    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
  } catch {
    toast.error("Failed");
  }
};


  useEffect(() => {
    if (!userID) return;
    loadDeliveryProvider();
    loadBanks();
    loadOrg();
  }, [userID]); // eslint-disable-line

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <SectionCard icon={Building2} title="Business Information">
        <div className="space-y-3">
          <div><Label>Organization Name</Label><Input value={orgForm.organizationName} onChange={(e)=>setOrgForm(s=>({...s,organizationName:e.target.value}))} /></div>
          <div><Label>GST Number (optional)</Label><Input value={orgForm.gstNumber} onChange={(e)=>setOrgForm(s=>({...s,gstNumber:e.target.value}))} /></div>
          <div>
            <Label>Payment Gateway</Label>
            <Select value={orgForm.payment_gateway} onChange={(e)=>setOrgForm(s=>({...s,payment_gateway:e.target.value}))}>
              <option value="RAZORPAY">Razorpay</option>
              <option value="PAYPAL">Paypal</option>
              <option value="BUMPPYPAY">Bumppy Pay</option>
            </Select>
          </div>
          <div><Label>Brand Image (Base64 or URL)</Label><Input value={orgForm.brand_image} onChange={(e)=>setOrgForm(s=>({...s,brand_image:e.target.value}))} placeholder="Paste base64 data URL or image URL" /></div>
          <button onClick={saveOrg} className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            Save
          </button>
        </div>
      </SectionCard>

      <SectionCard icon={ShieldCheck} title="Delivery Provider">
        <div className="grid grid-cols-1 gap-3">
          {["RESTAURANT", "PACKKY", "DOORDASH"].map((p) => (
            <button
              key={p}
              onClick={() => setProvider(p)}
              className={
                "w-full rounded-lg border px-3 py-2 text-sm font-semibold " +
                (deliveryType === p ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-gray-200")
              }
            >
              {p === "RESTAURANT" ? "Restaurant Delivery Service" : p === "PACKKY" ? "Packky Delivery Service" : "DoorDash Delivery Service"}
            </button>
          ))}
        </div>
      </SectionCard>

      <SectionCard icon={Wallet} title="Settlement Bank Accounts">
        <div className="space-y-3">
          <div className="rounded-lg border border-gray-100 p-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Account Number</Label><Input value={bankForm.account} onChange={(e)=>setBankForm(s=>({...s,account:e.target.value}))} /></div>
              <div><Label>IFSC</Label><Input value={bankForm.ifsc} onChange={(e)=>setBankForm(s=>({...s,ifsc:e.target.value}))} /></div>
              <div><Label>Bank Name</Label><Input value={bankForm.bankName} onChange={(e)=>setBankForm(s=>({...s,bankName:e.target.value}))} /></div>
              <div><Label>Account Holder</Label><Input value={bankForm.bankHolderName} onChange={(e)=>setBankForm(s=>({...s,bankHolderName:e.target.value}))} /></div>
            </div>
            <button onClick={addBank} className="mt-3 w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
              Add Account
            </button>
          </div>

          <div className="space-y-2">
            {bankList.length === 0 && <div className="text-sm text-gray-500">No bank accounts</div>}
            {bankList.map((b) => (
              <div key={b.id} className="flex items-center justify-between rounded-lg border p-3 text-sm">
                <div>
                  <div className="font-medium">{b.us_account}</div>
                  <div className="text-xs text-gray-500">{b.bank_name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
